import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { currentMonth, monthLabel, monthOptions } from '@/lib/months';
import { useLeadQuality, useProjects } from '@/lib/queries';
import { useCompanyScope } from '@/lib/useCompanyScope';
import { MANAGER_ROLES, type QualityNode } from '@performance-crm/shared';

const GROUP_LABEL: Record<string, string> = {
  NO_GESTIONADO: 'No gestionado',
  DUPLICADO: 'Duplicado / recontacto',
  GESTIONADO: 'Gestionado',
  OTRO: 'Otro',
};
const GROUPS = ['GESTIONADO', 'NO_GESTIONADO', 'DUPLICADO', 'OTRO'];
const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);

export function LeadQualityPage() {
  const { user } = useAuth();
  const { companies: allCompanies, active } = useCompanyScope();
  // El reporte muestra cifras de todo el proyecto: solo en las empresas que el usuario gestiona.
  const companies = allCompanies.filter((c) =>
    (user?.memberships ?? []).some(
      (m) => m.companyId === c.companyId && MANAGER_ROLES.includes(m.role),
    ),
  );
  const [companyId, setCompanyId] = useState(() =>
    companies.some((c) => c.companyId === active) ? active : companies[0]?.companyId,
  );
  const [projectId, setProjectId] = useState('');
  // Cambiar la empresa en la cabecera también cambia el reporte (antes se quedaba en la anterior).
  useEffect(() => {
    if (active && active !== companyId && companies.some((c) => c.companyId === active)) {
      setCompanyId(active);
      setProjectId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo cuando cambia la empresa activa
  }, [active]);
  const [month, setMonth] = useState(currentMonth());
  const { data: projects } = useProjects();
  const { data, isLoading } = useLeadQuality(companyId, month, projectId || undefined);
  if (user && companies.length === 0) return <Navigate to="/" replace />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Calidad de leads</h1>
        <p className="text-muted-foreground text-sm">
          Leads contactados y recontacto de los últimos 12 meses, y árbol de leads digitales por
          medio y audiencia (utm_medium) del mes.
        </p>
      </div>
      <div className="bg-card grid gap-2 rounded-lg border p-3 md:grid-cols-3">
        {companies.length > 1 && (
          <Select
            value={companyId ?? ''}
            onChange={(e) => {
              setCompanyId(e.target.value);
              setProjectId('');
            }}
            aria-label="Empresa"
          >
            {companies.map((c) => (
              <option key={c.companyId} value={c.companyId}>
                {c.companyName}
              </option>
            ))}
          </Select>
        )}
        <Select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          aria-label="Proyecto"
        >
          <option value="">Todos los proyectos</option>
          {projects
            ?.filter((p) => p.companyId === companyId)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </Select>
        <Select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Mes">
          {monthOptions().map((m) => (
            <option key={m} value={m}>
              {monthLabel(m)}
            </option>
          ))}
        </Select>
      </div>
      {isLoading && <Skeleton className="h-64 w-full" />}
      {data && (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>% de leads contactados</CardTitle>
                <CardDescription>
                  Leads captados en el mes que ya tuvieron un contacto efectivo ÷ leads captados en
                  el mes: se cuenta por lead (nunca pasa de 100 %). No es el «% contacto efectivo»
                  del Embudo y el ranking, que divide gestiones efectivas entre gestiones de
                  contacto.
                </CardDescription>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={data.monthly.map((m) => ({
                      month: monthLabel(m.month),
                      contactados:
                        m.pctContactados === null ? null : Math.round(m.pctContactados * 100),
                      recontacto:
                        m.pctRecontacto === null ? null : Math.round(m.pctRecontacto * 100),
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" fontSize={11} />
                    <YAxis fontSize={11} unit="%" />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="contactados"
                      name="% leads contactados"
                      stroke="#2563eb"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="recontacto"
                      name="% recontacto"
                      stroke="#d97706"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Captaciones y únicos</CardTitle>
              </CardHeader>
              <CardContent className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.monthly.map((m) => ({
                      month: monthLabel(m.month),
                      captaciones: m.captaciones,
                      unicos: m.leadsUnicos,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="captaciones" name="Captaciones" fill="#93c5fd" />
                    <Bar dataKey="unicos" name="Leads únicos" fill="#2563eb" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TreeCard
              title={`Leads digitales por medio · ${data.tree.totalDigital}`}
              nodes={data.tree.byCategory}
              total={data.tree.totalDigital}
            />
            <TreeCard
              title="Leads digitales por audiencia (utm_medium)"
              nodes={data.tree.byAudience}
              total={data.tree.totalDigital}
            />
          </div>
          <table className="bg-card w-full overflow-hidden rounded-lg border text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase">
              <tr>
                <th className="px-3 py-2">Mes</th>
                <th className="px-3 py-2 text-right">Captaciones</th>
                <th className="px-3 py-2 text-right">Únicos</th>
                <th className="px-3 py-2 text-right">Recontacto</th>
                <th className="px-3 py-2 text-right">% recontacto</th>
                <th className="px-3 py-2 text-right">Leads contactados</th>
                <th className="px-3 py-2 text-right">% leads contactados</th>
              </tr>
            </thead>
            <tbody>
              {data.monthly.map((m) => (
                <tr key={m.month} className="border-t">
                  <td className="px-3 py-1.5">{monthLabel(m.month)}</td>
                  <td className="px-3 py-1.5 text-right">{m.captaciones}</td>
                  <td className="px-3 py-1.5 text-right">{m.leadsUnicos}</td>
                  <td className="px-3 py-1.5 text-right">{m.recontacto}</td>
                  <td className="px-3 py-1.5 text-right">{pct(m.pctRecontacto)}</td>
                  <td className="px-3 py-1.5 text-right">{m.leadsContactados}</td>
                  <td className="px-3 py-1.5 text-right">{pct(m.pctContactados)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function TreeCard({ title, nodes, total }: { title: string; nodes: QualityNode[]; total: number }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>Cada barra se divide por agrupación de sub-estado.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2" data-testid="quality-tree">
        {nodes.length === 0 && (
          <p className="text-muted-foreground text-sm">Sin leads digitales en el mes.</p>
        )}
        {nodes.slice(0, 12).map((n) => (
          <div key={n.key} className="text-sm">
            <div className="flex justify-between">
              <span className="font-medium">{n.key}</span>
              <span className="text-muted-foreground">
                {n.total} · {total ? Math.round((n.total / total) * 100) : 0}%
              </span>
            </div>
            <div className="bg-muted mt-1 flex h-3 overflow-hidden rounded">
              {GROUPS.map((g) => {
                const v = n.groups[g] ?? 0;
                if (!v) return null;
                const color =
                  g === 'GESTIONADO'
                    ? 'bg-emerald-500'
                    : g === 'NO_GESTIONADO'
                      ? 'bg-amber-500'
                      : g === 'DUPLICADO'
                        ? 'bg-slate-400'
                        : 'bg-violet-400';
                return (
                  <div
                    key={g}
                    className={color}
                    style={{ width: `${(v / n.total) * 100}%` }}
                    title={`${GROUP_LABEL[g]}: ${v}`}
                  />
                );
              })}
            </div>
            <p className="text-muted-foreground mt-0.5 text-[11px]">
              {GROUPS.filter((g) => n.groups[g])
                .map((g) => `${GROUP_LABEL[g]} ${n.groups[g]}`)
                .join(' · ')}
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
